import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { fetchUsers } from "../api/userApi";
import { POLISH_CITY_OPTIONS } from "../data/polishCities";
import PhotoCarousel from "./PhotoCarousel";
import UserBio from "./UserBio";
import BuyUnitsModal from "./BuyUnitsModal";
import "./BrowseUsers.css";

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [30, 60, 150];
const EMPTY_FILTERS = {
  online: "",
  country: "",
  city: "",
  gender: "",
  minAge: "",
  maxAge: ""
};

export function getPaginationItems(totalPages, currentPage) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const safeCurrentPage = Math.max(0, Math.min(currentPage, totalPages - 1));
  const pages = new Set([0, totalPages - 1, safeCurrentPage]);

  if (safeCurrentPage <= 3) {
    for (let index = 1; index <= 4; index += 1) {
      pages.add(index);
    }
  } else if (safeCurrentPage >= totalPages - 4) {
    for (let index = totalPages - 5; index < totalPages - 1; index += 1) {
      pages.add(index);
    }
  } else {
    pages.add(safeCurrentPage - 1);
    pages.add(safeCurrentPage + 1);
  }

  const sortedPages = Array.from(pages).sort((first, second) => first - second);
  return sortedPages.reduce((items, pageNumber, index) => {
    if (index > 0 && pageNumber - sortedPages[index - 1] > 1) {
      items.push(`ellipsis-${pageNumber}`);
    }
    items.push(pageNumber);
    return items;
  }, []);
}

function getErrorMessage(errorData, fallbackMessage) {
  if (!errorData) return fallbackMessage;
  if (typeof errorData === "string") return errorData;
  if (typeof errorData.message === "string") return errorData.message;
  if (typeof errorData.error === "string") return errorData.error;
  return fallbackMessage;
}

function BrowseUsers() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState(null);
  const [buyUnitsOpen, setBuyUnitsOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [matchLimitModalOpen, setMatchLimitModalOpen] = useState(false);
  const { token, logout } = useAuth();
  const { t } = useI18n();

  useEffect(() => {
    async function loadUsers() {
      if (!token) {
        setError(t("auth.login"));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        setSuccessMessage("");
        const response = await fetchUsers({ token, filters, page, size: pageSize });
        const data = response.data;

        if (Array.isArray(data)) {
          const totalPages = Math.ceil(data.length / pageSize);
          const safePage = totalPages > 0 ? Math.min(page, totalPages - 1) : 0;
          const start = safePage * pageSize;

          setUsers(data.slice(start, start + pageSize));
          setPagination({
            totalElements: data.length,
            totalPages,
            number: safePage,
            size: pageSize,
            first: safePage === 0,
            last: totalPages === 0 || safePage >= totalPages - 1
          });

          if (page !== safePage) {
            setPage(safePage);
          }

          return;
        }

        const nextPagination = {
          totalElements: Number(data?.totalElements) || 0,
          totalPages: Number(data?.totalPages) || 0,
          number: Number(data?.number) || page,
          size: Number(data?.size) || pageSize,
          first: Boolean(data?.first),
          last: Boolean(data?.last)
        };
        setUsers(Array.isArray(data?.content) ? data.content : []);
        setPagination(nextPagination);
        if (page > 0 && nextPagination.totalPages > 0 && page >= nextPagination.totalPages) {
          setPage(nextPagination.totalPages - 1);
        }
      } catch (err) {
        console.error("Browse users error:", err);
        if (err.response?.status === 401) {
          logout();
          setError(t("auth.login"));
        } else if (err.response?.data) {
          setError(getErrorMessage(err.response.data, "Failed to load users."));
        } else if (err.message) {
          setError(`Failed to load users: ${err.message}`);
        } else {
          setError("Failed to load users.");
        }
      } finally {
        setLoading(false);
      }
    }

    loadUsers();
  }, [token, logout, filters, page, pageSize]);

  async function handleLike(userId) {
    if (!userId) {
      setError("Cannot create match: target user id is missing.");
      return;
    }

    try {
      setError("");
      setSuccessMessage("");
      const response = await axios.post(
        `/api/likes/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((previousUsers) => previousUsers.filter((user) => user.id !== userId));
      if (response.data?.status === "MATCH_CREATED") {
        setSuccessMessage(t("browse.matchCreatedWithChat"));
      } else {
        setSuccessMessage(t("browse.matchCreated"));
      }
    } catch (err) {
      console.error("Create match error:", err);
      const backendMessage = getErrorMessage(err.response?.data, "Failed to create match.");
      if (err.response?.status === 401) {
        logout();
        setError(t("auth.login"));
      } else if (err.response?.status === 403) {
        setError(`Access denied: ${backendMessage}`);
      } else if (err.response?.status === 404) {
        setError(`User not found: ${backendMessage}`);
      } else if (err.response?.status === 429) {
        setMatchLimitModalOpen(true);
      } else if (err.response?.status === 409) {
        setUsers((previousUsers) => previousUsers.filter((user) => user.id !== userId));
        setError(backendMessage || "You are already matched with this user.");
      } else {
        setError(err.message ? `Failed to create match: ${err.message}` : backendMessage);
      }
    }
  }

  function updateDraftFilter(event) {
    const { name, value, checked, type } = event.target;
    setDraftFilters((current) => ({
      ...current,
      [name]: type === "checkbox" ? (checked ? "true" : "") : value
    }));
  }

  function applyFilters(event) {
    event.preventDefault();
    setFilters({ ...draftFilters });
    setPage(0);
  }

  function resetFilters() {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(0);
  }

  function changePageSize(event) {
    setPageSize(Number(event.target.value));
    setPage(0);
  }

  function getUserInitial(username) {
    return username ? username.charAt(0).toUpperCase() : "?";
  }

  function getUserPhotoUrl(user) {
    return user.mainPhotoUrl || user.photoUrl || "";
  }

  function getUserPhotoUrls(user) {
    if (Array.isArray(user.photoUrls) && user.photoUrls.length > 0) {
      return user.photoUrls.filter(Boolean);
    }
    return [getUserPhotoUrl(user)].filter(Boolean);
  }

  function openPhoto(user, photoUrl) {
    setSelectedPhoto({
      url: photoUrl,
      alt: user.username || "User photo"
    });
  }

  function renderPagination() {
    if (!pagination || pagination.totalPages <= 1) return null;
    const paginationItems = getPaginationItems(pagination.totalPages, page);

    return (
      <nav className="pagination" aria-label={t("browse.title")}>
        <button type="button" onClick={() => setPage((current) => current - 1)} disabled={pagination.first}>
          {t("browse.previous")}
        </button>
        <div className="pagination-pages">
          {paginationItems.map((item) => (
            typeof item === "string" ? (
              <span className="pagination-ellipsis" key={item} aria-hidden="true">…</span>
            ) : (
              <button
                type="button"
                key={item}
                className={item === page ? "active" : ""}
                aria-current={item === page ? "page" : undefined}
                onClick={() => setPage(item)}
              >
                {item + 1}
              </button>
            )
          ))}
        </div>
        <button type="button" onClick={() => setPage((current) => current + 1)} disabled={pagination.last}>
          {t("browse.next")}
        </button>
      </nav>
    );
  }

  if (loading) {
    return (
      <div className="browse-page">
        <div className="browse-header">
          <h1 className="browse-title">{t("browse.title")}</h1>
          <p className="browse-subtitle">{t("browse.loading")}</p>
        </div>
        <div className="empty-state"><p>{t("browse.finding")}</p></div>
      </div>
    );
  }

  return (
    <div className="browse-page">
      <div className="browse-header">
        <div className="browse-title-row">
          <h1 className="browse-title">{t("browse.title")}</h1>
          <button
            type="button"
            className="buy-units-header-button"
            onClick={() => setBuyUnitsOpen(true)}
          >
            {t("browse.buyUnits")}
          </button>
        </div>
        <p className="browse-subtitle">{t("browse.subtitle")}</p>
      </div>

      {error && <div className="browse-message browse-error">{String(error)}</div>}
      {successMessage && <div className="browse-message browse-success">{String(successMessage)}</div>}

      <form className="browse-filters" onSubmit={applyFilters}>
        <div className="browse-filters-heading">
          <div>
            <span className="browse-section-eyebrow">{t("browse.title")}</span>
            <h2>{t("browse.filterHeading")}</h2>
          </div>
          <span className="browse-filter-hint">{t("browse.filterHint")}</span>
        </div>

        <div className="browse-filter-grid">
          <label className="browse-online-filter">
            <span><input type="checkbox" name="online" checked={draftFilters.online === "true"} onChange={updateDraftFilter} /> {t("browse.onlineNow")}</span>
          </label>
          <label htmlFor="browse-country">{t("browse.country")}<select id="browse-country" name="country" value={draftFilters.country} onChange={updateDraftFilter}><option value="">{t("browse.anyCountry")}</option><option value="Poland">Poland</option></select></label>
          <label>{t("browse.city")}<input name="city" list="polish-cities" value={draftFilters.city} onChange={updateDraftFilter} placeholder={t("browse.searchCity")} /></label>
          <datalist id="polish-cities">{POLISH_CITY_OPTIONS.map(({ label, value }) => <option key={value} value={label} />)}</datalist>
          <label>{t("browse.gender")}<select name="gender" value={draftFilters.gender} onChange={updateDraftFilter}><option value="">{t("browse.anyGender")}</option><option value="FEMALE">{t("browse.female")}</option><option value="MALE">{t("browse.male")}</option><option value="OTHER">{t("browse.other")}</option></select></label>
          <label>{t("browse.minAge")}<input name="minAge" type="number" min="18" max="100" value={draftFilters.minAge} onChange={updateDraftFilter} /></label>
          <label htmlFor="browse-max-age">{t("browse.maxAge")}<input id="browse-max-age" name="maxAge" type="number" min="18" max="120" value={draftFilters.maxAge} onChange={updateDraftFilter} /></label>
        </div>

        <div className="filter-actions">
          <button type="submit">{t("browse.applyFilters")}</button>
          <button type="button" className="filter-reset-button" onClick={resetFilters}>{t("browse.reset")}</button>
          <label className="results-size-field">{t("browse.resultsPerPage")}<select value={pageSize} onChange={changePageSize}>{PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
        </div>
      </form>

      {pagination && (
        <div className="browse-results-summary">
          <p className="browse-total">{t("browse.usersFound", { count: pagination.totalElements })}</p>
          <span>{t("browse.resultsPage", { page: page + 1 })}</span>
        </div>
      )}

      {users.length === 0 && !error && (
        <div className="empty-state">
          <h2>{t("browse.noUsers")}</h2>
          <p>{t("browse.noMoreUsers")}</p>
          <Link to="/matches" className="matches-link-button">{t("browse.goToMatches")}</Link>
        </div>
      )}

      {users.length > 0 && (
        <div className="users-grid">
          {users.map((user) => {
            const online = user?.online === true;
            const details = [
              user.age !== undefined && user.age !== null ? `${user.age} years` : null,
              user.gender,
              user.city,
              user.country
            ].filter(Boolean).join(" • ");
            return (
              <div key={user.id} className="user-card">
                <div className="user-photo-wrap"><PhotoCarousel photos={getUserPhotoUrls(user)} fallbackInitial={getUserInitial(user.username)} alt={user.username || "User photo"} className="browse-photo-carousel" onPhotoClick={(photoUrl) => openPhoto(user, photoUrl)} /></div>
                <div className="user-card-body">
                  <div className="user-name-row">
                    <h2 className="user-name">{user.username}</h2>
                    <span className={`profile-status-badge ${online ? "online" : "offline"}`} title={online ? t("browse.online") : t("browse.offline")} aria-label={online ? t("browse.online") : t("browse.offline")}><span className="profile-status-dot" /><span>{online ? t("browse.online") : t("browse.offline")}</span></span>
                  </div>
                  <p className="user-meta" title={details}>{details || t("browse.profileDetailsMissing")}</p>
                  <UserBio bio={user.bio} username={user.username} />
                  <div className="user-actions">
                    <button type="button" className="like-button" onClick={() => handleLike(user.id)}>{t("browse.like")}</button>
                    {user.videoUrl && <a href={user.videoUrl} target="_blank" rel="noreferrer" className="video-link">{t("browse.viewVideo")}</a>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {users.length > 0 && (
        <div className="browse-footer">
          <Link to="/matches" className="matches-link-button">{t("browse.goToMatches")}</Link>
          {renderPagination()}
        </div>
      )}
      <BuyUnitsModal
        isOpen={buyUnitsOpen}
        onClose={() => setBuyUnitsOpen(false)}
      />
      {selectedPhoto && (
        <div
          className="browse-photo-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedPhoto(null);
            }
          }}
        >
          <div className="browse-photo-modal" role="dialog" aria-modal="true" aria-label={selectedPhoto.alt}>
            <button
              type="button"
              className="browse-photo-modal-close"
              aria-label="Close photo"
              onClick={() => setSelectedPhoto(null)}
            >
              ×
            </button>
            <img src={selectedPhoto.url} alt={selectedPhoto.alt} />
          </div>
        </div>
      )}
      {matchLimitModalOpen && (
        <div
          className="browse-match-limit-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setMatchLimitModalOpen(false);
            }
          }}
        >
          <div
            className="browse-match-limit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="browse-match-limit-title"
          >
            <div className="browse-match-limit-icon" aria-hidden="true">!</div>
            <h2 id="browse-match-limit-title">{t("browse.dailyLimitTitle")}</h2>
            <p>{t("browse.dailyLimitMessage")}</p>
            <button type="button" onClick={() => setMatchLimitModalOpen(false)}>
              {t("browse.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrowseUsers;
